import { Map } from 'immutable';
import isElectron from 'is-electron';
import { signOut, registerElectronArgs } from 'flow/actions/app-actions';
import { releaseCompanionRoleOnLogout } from 'flow/companion/companion-actions';
import { store } from 'flow/configure-store';
import { getBrand } from 'flow/brands/brands';
import {
    waitForFinishExternalSignIn,
    waitForSocialIntegrationSettings,
} from 'flow/actions/social-integration-actions';
import { MICROPHONE, permissionTypes } from 'flow/utils/media-utils';
import { addMeetingLog, MeetingLogLevel } from 'flow/utils/meeting-utils';
import {
    navigateTo,
    navigateToMeetingLobby,
} from 'flow/actions/navigate-actions';
import {
    getNumLocalCalls,
    getPhoneIsMuted,
    isSessionOnHold,
    getFocusedLocalCallId,
} from 'flow/selectors/call-selectors';
import { addGeneralLogEntry } from 'flow/utils/browser-utils';

const ipcRenderer = isElectron() ? require('electron').ipcRenderer : null;
const webFrame = isElectron() ? require('electron').webFrame : null;

let globalZoomLevel = 0;
// Add a flag to prevent zoom reset during intentional zoom operations
let isSettingZoom = false;
// Add a lock to prevent multiple resize handlers from running simultaneously
let zoomLock = false;
// Add a timestamp to track the last manual zoom change
let lastManualZoomTimestamp = 0;
// The minimum time that must pass before a resize event can affect zoom (3 seconds)
const ZOOM_PROTECTION_PERIOD_MS = 3000;
export const DELETE_INTEGRATION_TOKEN_TIMEOUT_MS = 5000;

/**
 * Sets zoom level globally for the application and stores it
 * @param {number} zoomLevel - The zoom level to set
 */
export const setGlobalZoomLevel = (zoomLevel) => {
    if (isElectron() && webFrame) {
        // Set flag to prevent resize handler from interfering
        isSettingZoom = true;
        zoomLock = true;

        lastManualZoomTimestamp = Date.now();

        globalZoomLevel = zoomLevel;
        webFrame.setZoomLevel(zoomLevel);
        localStorage.setItem('globalZoomLevel', zoomLevel.toString());

        if (ipcRenderer) {
            ipcRenderer.send('update-zoom-level', zoomLevel);
            ipcRenderer.send('zoom-operation-start');
        }

        setTimeout(() => {
            isSettingZoom = false;
            zoomLock = false;
            if (ipcRenderer) {
                ipcRenderer.send('zoom-operation-end');
            }
        }, 500);
    }
};

/**
 * Gets the current global zoom level
 * @returns {number} The current zoom level
 */
export const getGlobalZoomLevel = () => {
    if (isElectron() && webFrame) {
        return globalZoomLevel;
    }
    return 0;
};

/**
 * Initializes the zoom level from storage or defaults to 0
 */
export const initializeZoomLevel = () => {
    if (isElectron() && webFrame) {
        // Prevent any resize events from triggering while we initialize
        zoomLock = true;

        const storedZoomLevel = localStorage.getItem('globalZoomLevel');
        if (storedZoomLevel !== null) {
            const zoomLevel = parseFloat(storedZoomLevel);
            globalZoomLevel = zoomLevel;
            webFrame.setZoomLevel(zoomLevel);

            if (ipcRenderer) {
                ipcRenderer.send('update-zoom-level', zoomLevel);
            }
        }

        setTimeout(() => {
            zoomLock = false;
        }, 500);
    }
};

/**
 * Increases the zoom level by 0.5
 */
export const zoomIn = () => {
    if (isElectron() && webFrame) {
        const newZoomLevel = globalZoomLevel + 0.5;
        setGlobalZoomLevel(newZoomLevel);
    }
};

/**
 * Decreases the zoom level by 0.5
 */
export const zoomOut = () => {
    if (isElectron() && webFrame) {
        const newZoomLevel = globalZoomLevel - 0.5;
        setGlobalZoomLevel(newZoomLevel);
    }
};

/**
 * Resets the zoom level to 0 (100%)
 */
export const resetZoom = () => {
    if (isElectron() && webFrame) {
        setGlobalZoomLevel(0);
    }
};

// Set up listeners for zoom events from main process
if (isElectron() && ipcRenderer) {
    ipcRenderer.on('apply-zoom-level', (_, zoomLevel) => {
        // Only apply zoom level from main process if we are not currently setting zoom manually
        // and the zoom level is different from what we already have
        if (webFrame && !isSettingZoom && globalZoomLevel !== zoomLevel) {
            globalZoomLevel = zoomLevel;
            webFrame.setZoomLevel(zoomLevel);
            localStorage.setItem('globalZoomLevel', zoomLevel.toString());
        }
    });

    // Set up listener for theme changes from main process (Electron menu)
    ipcRenderer.on('set-theme', (_, theme) => {
        // Apply theme change from Electron menu
        if (theme === 'auto') {
            const prefersDark = window.matchMedia(
                '(prefers-color-scheme: dark)',
            ).matches;
            document.documentElement.classList.toggle('dark', prefersDark);
        } else {
            document.documentElement.classList.toggle('dark', theme === 'dark');
        }

        // Save theme preference to localStorage
        localStorage.setItem('theme', theme);

        // Dispatch a custom event so components can react to theme changes
        window.dispatchEvent(
            new CustomEvent('theme-changed', { detail: theme }),
        );
    });
}

// Listen for route changes to maintain zoom level
export const setupRouteChangeHandler = () => {
    if (isElectron() && webFrame && !isSettingZoom && !zoomLock) {
        const storedZoomLevel = localStorage.getItem('globalZoomLevel');
        if (storedZoomLevel !== null) {
            const zoomLevel = parseFloat(storedZoomLevel);

            if (globalZoomLevel !== zoomLevel) {
                globalZoomLevel = zoomLevel;
                webFrame.setZoomLevel(zoomLevel);
            }
        }
    }
};

/**
 * Function to check if enough time has passed since the last manual zoom change
 * @returns {boolean} True if we're outside the protection period
 */
const isOutsideZoomProtectionPeriod = () => {
    const timeSinceLastZoom = Date.now() - lastManualZoomTimestamp;
    const result = timeSinceLastZoom > ZOOM_PROTECTION_PERIOD_MS;
    return result;
};

/**
 * Function to handle window resize with protection against accidental zoom resets
 */
export const handleWindowResize = () => {
    // Skip completely if zoom is locked or we are in the middle of setting zoom
    if (isSettingZoom || zoomLock) {
        return;
    }

    // Skip resize handling if we're within the protection period after a manual zoom change
    if (!isOutsideZoomProtectionPeriod()) {
        return;
    }

    // Set lock to prevent multiple resize handlers from running simultaneously
    zoomLock = true;

    const currentWebFrameZoom = webFrame ? webFrame.getZoomLevel() : 0;

    if (
        isElectron() &&
        webFrame &&
        Math.abs(currentWebFrameZoom - globalZoomLevel) > 0.01
    ) {
        globalZoomLevel = currentWebFrameZoom;
        localStorage.setItem('globalZoomLevel', currentWebFrameZoom.toString());

        if (ipcRenderer) {
            ipcRenderer.send('update-zoom-level', currentWebFrameZoom);
        }
    }

    // Release the lock after a delay
    setTimeout(() => {
        zoomLock = false;
    }, 200);
};

/**
 * Force apply the current zoom level, regardless of locks or protection periods
 * This can be used when we know we need to enforce the zoom level
 */
export const forceApplyZoomLevel = () => {
    if (isElectron() && webFrame) {
        const storedZoomLevel = localStorage.getItem('globalZoomLevel');
        if (storedZoomLevel !== null) {
            const zoomLevel = parseFloat(storedZoomLevel);

            globalZoomLevel = zoomLevel;
            webFrame.setZoomLevel(zoomLevel);

            if (ipcRenderer) {
                ipcRenderer.send('update-zoom-level', zoomLevel);
            }
        }
    }
};

/**
 * Container for actions sent over channels to the ipcMain process
 */
export const electronActions = {
    setLoggedInCustomer: (customer) => {
        if (!isElectron()) return;

        ipcRenderer.send('set-logged-in-customer', customer);
    },
    saveUserCredentials: (username, password) => {
        if (!isElectron()) return;
        ipcRenderer.send('save-user-credentials', username, password);
    },
    deleteUserCredentials: () => {
        if (!isElectron()) {
            return;
        }
        ipcRenderer.send('delete-user-password');
    },
    deleteSavedUserName: () => {
        if (!isElectron()) {
            return;
        }
        ipcRenderer.send('delete-user-name');
    },
    getUserCredentials: () => {
        return new Promise((resolve, reject) => {
            if (!isElectron()) {
                resolve(null);
            }

            ipcRenderer.once('get-user-reply', (_event, credentials) => {
                if (
                    credentials &&
                    credentials.username &&
                    credentials.password
                ) {
                    resolve(Map(credentials));
                } else {
                    resolve(null);
                }
            });
            try {
                ipcRenderer.send('get-user');
            } catch (e) {
                reject(e);
            }
        });
    },
    sendTotalUnreadMessages: (_dispatch, unread) => {
        if (!isElectron()) return;

        ipcRenderer.send('update-badge', unread === 0 ? null : unread);
    },
    updateAndRestart: () => {
        if (!isElectron()) return;

        ipcRenderer.send('update-and-restart');
    },
    socialIntegrationSignIn: (url) => {
        if (!isElectron()) return;

        ipcRenderer.send('social-integration-signin', url);
    },
    startExternalSignIn: (signInUrl, syncSource) => {
        if (!isElectron()) return;

        const urlObj = new URL(signInUrl);
        if (!urlObj.searchParams.has('prompt')) {
            urlObj.searchParams.set('prompt', 'login');
        }
        const updatedUrl = urlObj.toString();
        ipcRenderer.send('external-sign-in', updatedUrl, syncSource);
    },
    initialComponentMounted: () => {
        if (!isElectron()) return;

        ipcRenderer.send('initial-component-mounted');
    },
    sendPhoneEnabled: (enabled) => {
        if (!isElectron()) return;

        ipcRenderer.send('phone-enabled', enabled);
    },
    // Tell the main process the softphone has been unregistered so it can
    // proceed with quitting (see registerUnregisterSoftphoneListener).
    notifySoftphoneUnregistered: () => {
        if (!isElectron()) return;

        ipcRenderer.send('softphone-unregistered');
    },
    sendUserBusy: () => {
        if (!isElectron()) return;

        ipcRenderer.send('user-busy');
    },
    sendUserPendingBusy: () => {
        if (!isElectron()) return;

        ipcRenderer.send('user-pending-busy');
    },
    sendUserAvailable: () => {
        if (!isElectron()) return;

        ipcRenderer.send('user-available');
    },
    sendUserUnavailable: () => {
        if (!isElectron()) return;

        ipcRenderer.send('user-unavailable');
    },
    sendUserLoggedOut: () => {
        if (!isElectron()) return;

        ipcRenderer.send('user-logged-out');
    },
    setLanguageForWindowsSpellChecker: (language) => {
        if (!isElectron()) return;
        ipcRenderer.send('set-application-language', language);
    },
    getSources: (options) => {
        return new Promise((resolve) => {
            if (!isElectron()) resolve(null);
            ipcRenderer
                .invoke('DESKTOP_CAPTURER_GET_SOURCES', options)
                .then((result) => {
                    resolve(result);
                });
        });
    },
    initJabra: () => {
        return new Promise((resolve) => {
            if (!isElectron()) {
                resolve(null);
            }
            const appName = getBrand().brandNameUIFriendly;
            const state = store.getState();
            const initialState = {
                ongoingCalls: getNumLocalCalls(state),
                muted: getPhoneIsMuted(state),
                onHold: isSessionOnHold(state, getFocusedLocalCallId(state)),
            };

            // Replace undefined values with null because undefined is not a valid value for ipcRenderer.invoke
            // IPC generally only supports serializable objects, and undefined is not serializable in this context.
            Object.keys(initialState).forEach((key) => {
                if (initialState[key] === undefined) {
                    initialState[key] = null;
                }
            });

            ipcRenderer
                .invoke('init-jabra', appName, initialState)
                .then((result) => {
                    resolve(result);
                });
        });
    },
    initYealink: () => {
        return new Promise((resolve) => {
            if (!isElectron()) resolve();

            ipcRenderer.invoke('yealink:init').then((deviceId) => {
                resolve(deviceId);
            });
        });
    },
    setupYealinkDevice: (deviceId) => {
        return new Promise((resolve) => {
            if (!isElectron()) resolve();

            ipcRenderer.invoke('yealink:setupDevice', deviceId).then(resolve);
        });
    },
    notifyThemeChanged: () => {
        if (!isElectron()) return;

        ipcRenderer.send('theme-changed-from-webapp');
    },
    /**
     * Methods to manage integration access token which will be
     * used with remember me login process
     */
    saveIntegrationToken: (tokenData) => {
        if (!isElectron()) return;
        const { accessToken, key } = tokenData || {};
        ipcRenderer.send('save-integration-token', accessToken, key);
    },
    getIntegrationToken: async () => {
        if (!isElectron()) return null;
        return ipcRenderer.invoke('get-integration-token');
    },
    getIntegrationTokenKey: () => {
        if (!isElectron()) return null;
        return ipcRenderer.invoke('get-integration-token-id');
    },
    // Never rejects and gives up after a timeout, so a stuck keychain cannot block logout.
    deleteIntegrationToken: async () => {
        if (!isElectron()) return;
        let timeoutId;
        const timeout = new Promise((_resolve, reject) => {
            timeoutId = setTimeout(
                () => reject(new Error('timed out')),
                DELETE_INTEGRATION_TOKEN_TIMEOUT_MS,
            );
        });
        try {
            await Promise.race([
                ipcRenderer.invoke('delete-integration-token'),
                timeout,
            ]);
        } catch (error) {
            addGeneralLogEntry(`Failed to delete integration token: ${error}`);
        } finally {
            clearTimeout(timeoutId);
        }
    },
    sendCompanionTrayCallState: (payload) => {
        if (!isElectron()) return;

        ipcRenderer.send('companion-tray-call-state', payload);
    },
    sendCompanionTrayProfiles: (payload) => {
        if (!isElectron()) return;

        ipcRenderer.send('companion-tray-profiles', payload);
    },
    // Whether this install is acting as the user's companion. The tray is the
    // companion's status surface on macOS, so main needs the answer to decide
    // whether to put an icon in the menubar at all.
    sendCompanionTrayDesignation: (payload) => {
        if (!isElectron()) return;

        ipcRenderer.send('companion-tray-designation', payload);
    },
};

/**
 * Container for creating channels that are listening to calls from the ipcMain process
 */
export const electronListeners = {
    registerSleepListener: (eventHandler) => {
        if (!isElectron()) return;

        ipcRenderer.on('system-sleep', eventHandler);
    },
    removeSleepListener: (eventHandler) => {
        if (!isElectron()) return;

        ipcRenderer.removeListener('system-sleep', eventHandler);
    },
    registerResumeListener: (eventHandler) => {
        if (!isElectron()) return;

        ipcRenderer.on('system-resume', eventHandler);
    },
    removeResumeListener: (eventHandler) => {
        if (!isElectron()) return;

        ipcRenderer.removeListener('system-resume', eventHandler);
    },
    registerLogoutListener: (dispatch) => {
        if (!isElectron()) return;

        // The macOS app-menu "Sign out", which reaches signOut directly rather than through
        // useNavigationMenu. Releasing matters more here than anywhere: this is the desktop
        // install, the only client that can hold the role at all, and the logout is deliberate.
        // The session is still good at this point - signOut is itself what clears the credentials
        // - so the release goes out before it, exactly as on the other path.
        ipcRenderer.on('log-out', async () => {
            await dispatch(releaseCompanionRoleOnLogout());
            dispatch(signOut());
        });
    },
    onUpdateAvailable: (callback) => {
        if (!isElectron()) return;

        ipcRenderer.on('update-available', () => {
            callback();
        });
    },
    onNoUpdateAvailable: (callback) => {
        if (!isElectron()) return;

        ipcRenderer.on('update-not-available', () => {
            callback();
        });
    },
    onUpdateError: (callback) => {
        if (!isElectron()) return;

        ipcRenderer.on('update-error', () => {
            callback();
        });
    },
    onUpdateDownloaded: (callback) => {
        if (!isElectron()) return;

        ipcRenderer.on('update-downloaded', () => {
            callback();
        });
    },
    registerArgsListener: (dispatch) => {
        if (!isElectron()) return;

        ipcRenderer.on('args-event', (_event, data) => {
            dispatch(registerElectronArgs(JSON.parse(data)));
        });
    },
    checkIfSocialIntegrationSignInFinished: (
        dispatch,
        extensionKey,
        syncSource,
    ) => {
        if (!isElectron()) return;

        ipcRenderer.once('check-social-integration-signin-finished', () => {
            dispatch(
                waitForSocialIntegrationSettings(extensionKey, syncSource),
            );
        });
    },
    checkIfExternalSignInFinished: (dispatch) => {
        if (!isElectron()) return;

        ipcRenderer.once(
            'check-external-signin-finished',
            async (_event, token, syncSource) => {
                await dispatch(waitForFinishExternalSignIn(token, syncSource));
            },
        );
    },
    registerPhoneEventListener: (eventHandler) => {
        if (!isElectron()) return;

        ipcRenderer.on('answer-hangup-call-event', eventHandler);
    },
    removePhoneEventListener: (eventHandler) => {
        if (!isElectron()) return;

        ipcRenderer.removeListener('answer-hangup-call-event', eventHandler);
    },
    registerTelLinkClickedListener: (eventHandler) => {
        if (!isElectron()) return;

        ipcRenderer.on('tel-link-clicked-event', eventHandler);
    },
    removeTelLinkClickedListener: (eventHandler) => {
        if (!isElectron()) return;

        ipcRenderer.removeListener('tel-link-clicked-event', eventHandler);
    },
    registerLogListener: (eventHandler) => {
        if (!isElectron()) return;

        ipcRenderer.on('log-event', (_event, logMessage, logType) => {
            eventHandler(logMessage, logType);
        });
    },
    removeLogListener: (eventHandler) => {
        if (!isElectron()) return;

        ipcRenderer.removeListener('log-event', eventHandler);
    },
    registerMeetingLobbyListener: (dispatch) => {
        if (!isElectron()) return;

        ipcRenderer.on('open-meeting-lobby', (_event, meetingCode) => {
            dispatch(navigateToMeetingLobby(meetingCode));
        });
    },
    registerDeepLinkListener: (dispatch) => {
        if (!isElectron()) return;

        ipcRenderer.on('deep-link-event', (_event, path) => {
            dispatch(navigateTo(path));
        });
    },
    // The main process asks us to unregister the softphone before it quits, so
    // the SIP registration is cleanly removed instead of being left as a ghost.
    registerUnregisterSoftphoneListener: (handler) => {
        if (!isElectron()) return;

        ipcRenderer.on('unregister-softphone', handler);
    },
    removeUnregisterSoftphoneListener: (handler) => {
        if (!isElectron()) return;

        ipcRenderer.removeListener('unregister-softphone', handler);
    },
    registerCompanionTrayAnswerListener: (eventHandler) => {
        if (!isElectron()) return;

        ipcRenderer.on('companion-tray-answer-call', eventHandler);
    },
    registerCompanionTrayHangupListener: (eventHandler) => {
        if (!isElectron()) return;

        ipcRenderer.on('companion-tray-hangup-call', eventHandler);
    },
    registerCompanionTrayActivateProfileListener: (eventHandler) => {
        if (!isElectron()) return;

        ipcRenderer.on('companion-tray-activate-profile', (_event, payload) => {
            eventHandler(payload);
        });
    },
    registerCompanionTrayOpenProfileModalListener: (eventHandler) => {
        if (!isElectron()) return;

        ipcRenderer.on(
            'companion-tray-open-profile-modal',
            (_event, payload) => {
                eventHandler(payload);
            },
        );
    },
};

/**
 * Check user microphone access on Mac
 * Only mac API
 * https://github.com/electron/electron/blob/v4.2.2/docs/api/system-preferences.md#systempreferencesgetmediaaccessstatusmediatype-macos
 */
export const checkElectronMicrophonePermission = async () => {
    if (!isElectron()) {
        return false;
    }

    const main = require('@electron/remote');
    const isMac = main && main.process && main.process.platform === 'darwin';
    if (!isMac) {
        // No permissions to ask for, good to go
        return true;
    }

    const getMicrophoneStatus =
        await main.systemPreferences.getMediaAccessStatus(MICROPHONE);
    if (getMicrophoneStatus === permissionTypes.DENIED) {
        return false;
    } else if (getMicrophoneStatus !== permissionTypes.GRANTED) {
        const askForMediaResult =
            await main.systemPreferences.askForMediaAccess(MICROPHONE);
        if (!askForMediaResult) {
            return false;
        }
    }
    return true;
};

/**
 * Get all screens available to screen share
 */
export const getElectronScreenshareSources = async () => {
    if (!isElectron()) {
        return false;
    }
    return await electronActions.getSources({
        types: ['screen', 'window'],
        thumbnailSize: { width: 187, height: 140 },
    });
};

export const filterScreenShareOptions = (allScreenShareOptions) => {
    const allScreens = allScreenShareOptions.filter((option) =>
        option.id.includes('screen'),
    );
    const allWindows = allScreenShareOptions.filter((option) =>
        option.id.includes('window'),
    );
    return { allScreens, allWindows };
};

/**
 * Check users screen recording rights
 * https://github.com/electron/electron/blob/v8.5.0/docs/api/system-preferences.md#systempreferencesgetmediaaccessstatusmediatype-macos
 *
 * Only macOS gates screen capture behind user consent. Windows always reports
 * it granted, and Linux has no getMediaAccessStatus at all — calling it there
 * threw, the rejection was swallowed, and the screen-share button stayed
 * disabled for good. Same shape as checkElectronMicrophonePermission above.
 */
export const checkElectronScreensharePermission = async () => {
    if (!isElectron()) {
        return false;
    }

    const main = require('@electron/remote');
    const isMac = main && main.process && main.process.platform === 'darwin';
    if (!isMac) {
        // No permissions to ask for, good to go
        return true;
    }

    const screenAccess =
        await main.systemPreferences.getMediaAccessStatus('screen');
    return screenAccess === permissionTypes.GRANTED;
};

/**
 * Start the screen share video stream
 */
export const getElectronScreenStream = async (sourceId) => {
    if (!isElectron() || !sourceId) {
        return false;
    }
    try {
        return await navigator.mediaDevices.getUserMedia({
            audio: false,
            video: {
                mandatory: {
                    chromeMediaSource: 'desktop',
                    chromeMediaSourceId: sourceId,
                },
            },
        });
    } catch (e) {
        addMeetingLog(
            `Error setting up local electron video feed: ${e}`,
            MeetingLogLevel.ERROR,
        );
        throw e;
    }
};

export const clearWebframeCache = () => {
    if (isElectron() && webFrame && webFrame.clearCache) {
        webFrame.clearCache();
        addGeneralLogEntry('Cleared webFrame cache (electron only)');
    }
};
